/**
 * 機能 1: エリア → 店舗フィルタ
 *
 * - 初期表示: エリア未選択 → 店舗 select は disabled
 * - エリア選択時: 該当エリアの店舗のみ表示
 * - エリア変更時: 現在の店舗選択が新エリアと不一致なら自動でリセット
 */

import { setSelectValue } from "./utils.js";

/**
 * エリア選択に応じた店舗フィルタの初期化。
 * shopAreaMap が空の場合は何もせず即 return（旧呼び出し箇所への後方互換）。
 *
 * @param {Object} params
 * @param {HTMLElement} params.shopWrapper - 店舗 select のラッパー（.c-forms-select）
 * @param {HTMLSelectElement} params.areaNative - エリア select の実 select 要素
 * @param {HTMLSelectElement} params.shopNative - 店舗 select の実 select 要素
 * @param {import('./utils.js').ShopAreaMap} params.shopAreaMap - 店舗 ID → エリア value の対応表
 * @returns {void}
 */
export const setupAreaShopFilter = ({ shopWrapper, areaNative, shopNative, shopAreaMap }) => {
    // 対応表が無ければ機能を無効化（既存呼び出し箇所への影響回避）
    if (Object.keys(shopAreaMap).length === 0) return;

    // カスタム select 関連の要素を取得
    const shopTrigger = shopWrapper.querySelector(".c-forms-select__trigger");
    const shopCustomOptions = shopWrapper.querySelectorAll(
        ".c-forms-select__dropdown .c-forms-select__option",
    );
    const shopNativeOptions = shopWrapper.querySelectorAll(
        ".c-forms-select__native option",
    );

    /**
     * 店舗 select の有効・無効を切替える。
     * カスタム UI（trigger）と実 select の両方に disabled を反映する。
     *
     * @param {boolean} disabled - true で無効化
     */
    const setShopDisabled = (disabled) => {
        // ラッパーの data-disabled でカスタム UI の見た目を切替
        shopWrapper.toggleAttribute("data-disabled", disabled);
        if (disabled) {
            shopTrigger.setAttribute("disabled", "");
            shopNative.setAttribute("disabled", "");
        } else {
            shopTrigger.removeAttribute("disabled");
            shopNative.removeAttribute("disabled");
        }
    };

    /**
     * 店舗 option の表示・非表示をエリアでフィルタする。
     * - 実 select の option: hidden + disabled で submit 対象外に
     * - カスタム option:    data-hidden で見た目を非表示に
     *
     * @param {string|number} areaValue - 現在選択されているエリアの value
     */
    const filterShops = (areaValue) => {
        // 実 select の option をフィルタ
        shopNativeOptions.forEach((opt) => {
            // 空 option（プレースホルダー）はスキップ
            if (opt.value === "") return;
            const matches = String(shopAreaMap[opt.value] ?? "") === String(areaValue);
            opt.hidden = !matches;
            opt.disabled = !matches;
        });

        // カスタム option をフィルタ
        shopCustomOptions.forEach((opt) => {
            const value = opt.dataset.value;
            if (value === "") return;
            const matches = String(shopAreaMap[value] ?? "") === String(areaValue);
            opt.toggleAttribute("data-hidden", !matches);
        });

        // 現在の店舗選択が新エリアと不一致ならリセット
        // （例: 千葉の店舗を選択した状態で栃木に切り替えた場合）
        const currentShop = shopNative.value;
        if (
            currentShop &&
            String(shopAreaMap[currentShop] ?? "") !== String(areaValue)
        ) {
            setSelectValue(shopNative, "");
        }
    };

    // ── 初期化処理 ──
    // ページロード時のエリア値（バリデーションエラーで戻った場合等）を考慮
    if (!areaNative.value) {
        // エリア未選択 → 店舗 select は触れない状態にする
        setShopDisabled(true);
    } else {
        // 既にエリアが選択済み → そのエリアでフィルタ済み状態にする
        filterShops(areaNative.value);
    }

    // ── イベントリスナー登録 ──
    areaNative.addEventListener("change", () => {
        const areaValue = areaNative.value;
        if (areaValue) {
            // エリアが選ばれた → 店舗 select を有効化してフィルタ
            setShopDisabled(false);
            filterShops(areaValue);
        } else {
            // プレースホルダーに戻された → 店舗 select を無効化
            setShopDisabled(true);
        }
    });
};
